import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Wine } from 'lucide-react';
import { toast } from 'sonner';

const RATINGS = [
  { key: 'excellent', label: 'Excellent', emoji: '🤩' },
  { key: 'good', label: 'Good', emoji: '😊' },
  { key: 'okay', label: 'Okay', emoji: '😐' },
  { key: 'bad', label: 'Bad', emoji: '😞' },
];

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Square redirects with ?order_id=<id>. Legacy/test redirects may use ?session_id.
  // The backend /payments/status/{id} accepts either.
  const lookupId = searchParams.get('order_id') || searchParams.get('session_id');
  const tabId = searchParams.get('tab_id');
  // Feb 2026: tab self-settle redirect carries `kind=tab_topup`. We poll the
  // tab status (closed) on success rather than the standard order/txn poll
  // because the customer's tab close happens server-side via the webhook
  // auto-settle path — the success message has to say "Tab settled" instead
  // of "Order confirmed" so the customer knows their visit is wrapped up.
  const kind = searchParams.get('kind');
  const isTabTopup = kind === 'tab_topup';
  const [status, setStatus] = useState('checking');
  const [orderId, setOrderId] = useState('');

  // Feedback prompt state
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!lookupId) { setStatus('error'); return; }
    let attempts = 0;
    const poll = async () => {
      // Square webhook can take a few seconds — poll more aggressively.
      if (attempts >= 20) { setStatus('timeout'); return; }
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${lookupId}`);
        if (data.payment_status === 'paid') {
          setStatus('success');
          setOrderId(data.order_id);
          return;
        }
        if (data.status === 'expired') { setStatus('expired'); return; }
        setTimeout(poll, 1500);
      } catch {
        setTimeout(poll, 1500);
      }
    };
    poll();
  }, [lookupId]);

  const submitFeedback = async () => {
    if (!rating || !orderId) return;
    setSubmitting(true);
    try {
      await api.post('/feedback', { order_id: orderId, rating, comment: comment.trim() });
      setSubmitted(true);
      toast.success(rating === 'excellent' || rating === 'good' ? 'Thanks! 5% off your next visit unlocked.' : 'Thanks for the feedback — we appreciate it.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center px-4 py-10">
      <div className="text-center max-w-sm w-full animate-fadeIn">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-gold/10 border border-gold/30 mb-6">
          <Wine className="w-7 h-7 text-gold" />
        </div>

        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-serif text-white mb-2">Processing Payment</h2>
            <p className="text-zinc-400 font-sans text-sm" data-testid="payment-checking">Verifying your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-serif text-white mb-2" data-testid="payment-success">
              {isTabTopup ? 'Tab Settled' : 'Payment Successful'}
            </h2>
            <p className="text-zinc-400 font-sans text-sm mb-6">
              {isTabTopup
                ? "Thank you — your tab is closed and the table will be cleared for you. Cheers!"
                : tabId
                  ? 'Your tab is now open! You can start ordering.'
                  : 'Your order has been confirmed and sent to preparation.'}
            </p>

            {/* Feedback prompt — show for completed in-house visits too
                (tab settle), not just one-shot orders. Skips the
                "deposit / tab-open" flow which uses tabId. Tab top-up
                feedback is gated by an explicit orderId check because
                the `/feedback` endpoint expects an order_id, and after
                a self-settle the customer has multiple orders — let
                them rate via the menu instead. */}
            {!tabId && !isTabTopup && orderId && !submitted && (
              <div className="bg-white/[0.03] border border-white/10 rounded-sm p-5 mb-5 text-left" data-testid="feedback-prompt">
                <p className="text-white font-serif text-base mb-1">How was it?</p>
                <p className="text-zinc-500 font-sans text-xs mb-4">A quick rating helps the team — and unlocks 5% off your next visit if you enjoyed it.</p>
                <div className="grid grid-cols-4 gap-1.5 mb-3">
                  {RATINGS.map(r => (
                    <button
                      key={r.key}
                      onClick={() => setRating(r.key)}
                      className={`py-2.5 rounded-sm border text-xs font-sans flex flex-col items-center gap-1 transition-all ${rating === r.key ? 'border-gold/60 bg-gold/10 text-gold' : 'border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200'}`}
                      data-testid={`feedback-rating-${r.key}`}
                    >
                      <span className="text-lg leading-none">{r.emoji}</span>
                      <span>{r.label}</span>
                    </button>
                  ))}
                </div>
                {rating && (
                  <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Anything else you'd like to share? (optional)"
                    className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 text-sm font-sans text-white focus:outline-none focus:border-gold/40 min-h-[60px] mb-3"
                    data-testid="feedback-comment"
                  />
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={submitFeedback}
                    disabled={!rating || submitting}
                    className="flex-1 bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans text-sm h-10 disabled:opacity-40"
                    data-testid="feedback-submit"
                  >
                    {submitting ? 'Sending...' : 'Submit'}
                  </Button>
                  <Button
                    onClick={() => setSubmitted(true)}
                    variant="ghost"
                    className="text-zinc-500 hover:text-zinc-300 font-sans text-xs h-10"
                    data-testid="feedback-skip"
                  >
                    Skip
                  </Button>
                </div>
              </div>
            )}

            {submitted && !tabId && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3 mb-5 text-emerald-400 font-sans text-xs" data-testid="feedback-thanks">
                Thanks for your feedback. See you again soon.
              </div>
            )}

            <Button
              onClick={() => {
                // Read the customer's last QR/takeaway path from localStorage so
                // tapping "Back to Menu" returns them to their cart — not the
                // Square checkout page (which is what `navigate(-1)` would do).
                let target = '/';
                try { target = localStorage.getItem('majnu:lastReturnPath') || '/'; } catch { /* private mode */ }
                // Auto-open the orders/cart view on landing so the customer sees their order status immediately
                try { sessionStorage.setItem('majnu:autoOpenCart', '1'); } catch { /* */ }
                navigate(target, { replace: true });
              }}
              className="bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans"
              data-testid="back-to-menu-button"
            >
              Back to Menu
            </Button>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-serif text-white mb-2" data-testid="payment-failed">Payment Issue</h2>
            <p className="text-zinc-400 font-sans text-sm mb-6">
              {status === 'timeout' ? 'Payment verification timed out. Please contact staff.' : 'There was an issue with your payment.'}
            </p>
            <Button
              onClick={() => {
                let target = '/';
                try { target = localStorage.getItem('majnu:lastReturnPath') || '/'; } catch { /* */ }
                navigate(target, { replace: true });
              }}
              className="bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans"
              data-testid="try-again-button"
            >
              Go Back
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
